// Le CHOIX du visiteur sur les cookies et le stockage local.
//
// Deux familles, et une seule se refuse :
//  - le cookie de session (`ro_session`) est strictement nécessaire : il n'est
//    pas soumis au choix, sans lui on ne peut pas rester connecté ;
//  - les PRÉFÉRENCES d'affichage (thème, vue en liste, filtres) sont du confort :
//    elles ne sont mémorisées dans le navigateur qu'après un « Accepter ».
//
// Tant que le visiteur n'a rien choisi, on n'écrit rien : le consentement vient
// AVANT le dépôt. Le choix lui-même est mémorisé (c'est permis : il faut bien
// se souvenir d'un refus pour ne pas reposer la question à chaque page).
import { useSyncExternalStore } from 'react';

export type CookieChoice = 'accepted' | 'refused';
const CHOICE_KEY = 'ro.cookies';
/** Toutes les préférences de l'application portent ce préfixe. */
const PREF_PREFIX = 'ro.';

// Stockage indisponible (navigation privée, stockage bloqué) : le choix vaut
// alors pour la page en cours, grâce à cette copie en mémoire.
let memory: CookieChoice | null = null;
const listeners = new Set<() => void>();

export function cookieChoice(): CookieChoice | null {
  if (memory) return memory;
  try {
    const v = localStorage.getItem(CHOICE_KEY);
    return v === 'accepted' || v === 'refused' ? v : null;
  } catch {
    return null;
  }
}

export function setCookieChoice(choice: CookieChoice | null) {
  memory = choice;
  try {
    if (choice) localStorage.setItem(CHOICE_KEY, choice);
    else localStorage.removeItem(CHOICE_KEY);
    // Refuser (ou revenir sur son choix) efface ce qui avait été mémorisé :
    // un refus qui laisserait les préférences en place n'en serait pas un.
    if (choice !== 'accepted') {
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith(PREF_PREFIX) && key !== CHOICE_KEY) localStorage.removeItem(key);
      }
    }
  } catch {
    /* stockage indisponible : rien à effacer, rien n'a pu être écrit */
  }
  listeners.forEach((notify) => notify());
}

/** Mémorise une préférence d'affichage — seulement si le visiteur a accepté. */
export function savePreference(key: string, value: string) {
  if (cookieChoice() !== 'accepted') return;
  try {
    localStorage.setItem(key, value);
  } catch {
    /* préférence non mémorisée : sans conséquence */
  }
}

function subscribe(notify: () => void) {
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
}

/** Le choix courant, tenu à jour : le bandeau disparaît dès qu'on a répondu. */
export function useCookieChoice(): CookieChoice | null {
  return useSyncExternalStore(subscribe, cookieChoice, () => null);
}
